import {
    Box,
    FormSelector,
    Icon,
    PargodaRoundButton,
    useAppSelector,
    useAppDispatch,
    useTheme,
} from '../../../../base';
import {
    FieldArrayWithId,
    useFormContext,
    UseFieldArrayReturn,
} from 'react-hook-form';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
    StyledTopControlsContainer,
    StyledFormContainer,
    StyledStepsListContainer,
    StyledPageHeader,
} from './JobInterviewSteps.styles';
import {
    IJobStepObject,
    ISaveJobForm,
    SAVE_JOB_FORM_VALUES,
} from '../../../../common';
import SortableStep from './SortableStep';
import { useTranslation } from 'react-i18next';
import { jobsActions, jobsSelectors } from '../../../../actions';
import { useEffect } from 'react';
import { useEffectOnce } from 'react-use';

const MAX_INTERVIEW_STEPS = 9;

interface Props {
    stepsArray: UseFieldArrayReturn<
        ISaveJobForm,
        SAVE_JOB_FORM_VALUES.STEPS,
        'id'
    >;
}

export const JobInterviewSteps = (props: Props) => {
    const { stepsArray } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const jobsList = useAppSelector(jobsSelectors.getJobsList);
    const activeJob = useAppSelector(jobsSelectors.getActiveJob);
    const copiedJobSteps = useAppSelector(jobsSelectors.getCopiedJobSteps);

    useEffectOnce(() => {
        dispatch(jobsActions.fetchJobsList());
        return () => {
            dispatch(jobsActions.setCopiedJobSteps({ steps: null }));
        };
    });

    const methods = useFormContext<ISaveJobForm>();
    const { control, watch, getValues } = methods;
    const { fields, remove, insert, move, replace } = stepsArray;

    const watchedSteps = watch(SAVE_JOB_FORM_VALUES.STEPS);
    const watchedSelectedJob = watch(SAVE_JOB_FORM_VALUES.STEPS_SELECTED_JOB);

    useEffect(() => {
        if (watchedSelectedJob?.value) {
            dispatch(
                jobsActions.fetchJobSteps({
                    jobId: watchedSelectedJob.value,
                })
            );
        }
    }, [watchedSelectedJob, dispatch]);

    useEffect(() => {
        if (!copiedJobSteps) {
            return;
        }

        const currentSteps =
            getValues(SAVE_JOB_FORM_VALUES.STEPS) || [];
        const firstStep =
            currentSteps.find((step) => step.isFirstStep) || currentSteps[0];
        const lastStep =
            currentSteps.find((step) => step.isLastStep) ||
            currentSteps[currentSteps.length - 1];

        if (!firstStep || !lastStep) {
            dispatch(jobsActions.setCopiedJobSteps({ steps: null }));
            return;
        }

        const middleSteps: Array<IJobStepObject> = copiedJobSteps
            .filter((step) => !step.isFirstStep && !step.isLastStep)
            .map((step, index) => ({
                id: `new_${Date.now()}_${index}`,
                stepName: step.name,
                stepOwner: step.stepOwner,
                interviewers: step.interviewers || [],
                isFirstStep: false,
                isLastStep: false,
            }));

        replace([firstStep, ...middleSteps, lastStep]);
        dispatch(jobsActions.setCopiedJobSteps({ steps: null }));
    }, [copiedJobSteps, dispatch, getValues, replace]);

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        const activeId = active.id;
        const overId = over?.id;

        if (overId && activeId !== overId) {
            const oldIndex = fields.findIndex((field) => field.id === activeId);
            const newIndex = fields.findIndex((field) => field.id === overId);

            if (oldIndex === -1 || newIndex === -1) {
                console.warn('Could not find dragged item index');
                return;
            }

            if (
                oldIndex === 0 ||
                newIndex === 0 ||
                oldIndex === fields.length - 1 ||
                newIndex === fields.length - 1
            ) {
                console.warn('Cannot move first or last step.');
                return;
            }

            const oldStepData: IJobStepObject = watchedSteps[oldIndex];
            const newStepData: IJobStepObject = watchedSteps[newIndex];
            if (oldStepData?.isFirstStep || newStepData?.isFirstStep) {
                console.warn(
                    'Cannot move or replace a fixed step (data check).'
                );
                return;
            }

            move(oldIndex, newIndex);
        }
    };

    const handleAddStep = () => {
        if (fields.length < MAX_INTERVIEW_STEPS) {
            const insertIndex = fields.length - 1;
            insert(insertIndex, {
                id: `new_${new Date().getTime()}`,
                stepName: '',
                stepOwner: {
                    label: activeJob?.recruiter
                        ? `${activeJob?.recruiter?.firstName} ${activeJob?.recruiter?.lastName}`
                        : `${activeJob?.creator?.firstName} ${activeJob?.creator?.lastName}`,
                    value:
                        activeJob?.recruiter?.id?.toString() ??
                        activeJob?.creator?.id?.toString() ??
                        null,
                    key:
                        activeJob?.recruiter?.id?.toString() ??
                        activeJob?.creator?.id?.toString(),
                },
                interviewers: [],
                isFirstStep: false,
                isLastStep: false,
            });
        }
    };

    const sortableItemIds = fields
        .filter((_, index) => index !== 0 && index !== fields.length - 1)
        .map((field) => field.id);

    const canAddMoreSteps = fields.length < MAX_INTERVIEW_STEPS;

    return (
        <StyledFormContainer>
            <StyledPageHeader>
                <Box sx={{ width: '300px' }}>
                    <FormSelector
                        name={SAVE_JOB_FORM_VALUES.STEPS_SELECTED_JOB}
                        control={control}
                        options={jobsList}
                        size="small"
                        label={t('saveJob.selectFromExistingJob')}
                        placeholder={t('saveJob.selectJobToCopyFrom')}
                    />
                </Box>
                <StyledTopControlsContainer>
                    <PargodaRoundButton
                        variant="contained"
                        onClick={handleAddStep}
                        disabled={!canAddMoreSteps}
                        startIcon={<Icon name="AddRoundedIcon" />}
                        sx={{
                            background: `linear-gradient(0deg,${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                            width: '180px',
                        }}
                    >
                        {t('saveJob.interviewStep')}
                    </PargodaRoundButton>
                </StyledTopControlsContainer>
            </StyledPageHeader>

            <StyledStepsListContainer>
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={sortableItemIds}
                        strategy={verticalListSortingStrategy}
                    >
                        {fields.map(
                            (
                                field: FieldArrayWithId<
                                    ISaveJobForm,
                                    SAVE_JOB_FORM_VALUES.STEPS,
                                    'id'
                                >,
                                index: number
                            ) => {
                                const stepData = watchedSteps[index];
                                const isFirst = stepData?.isFirstStep ?? false;
                                const isLast = stepData?.isLastStep ?? false;

                                return (
                                    <SortableStep
                                        key={field.id}
                                        fieldId={field.id}
                                        index={index}
                                        control={control}
                                        remove={remove}
                                        isFirstStep={isFirst}
                                        isLastStep={isLast}
                                    />
                                );
                            }
                        )}
                    </SortableContext>
                </DndContext>
            </StyledStepsListContainer>
        </StyledFormContainer>
    );
};

export default JobInterviewSteps;
